import { useCallback, useEffect } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const useCartMutationErrorNotice = (
  responseData: LineItemErrorResponseData,
) => {
  const errorMessage = responseData?.message;
  const responseType = responseData?.__typename;

  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── Errors ────────────────────────────────────────────────────────────────

  const isRestaurantMaxQuantityExceeded = responseType === 'RestaurantMaxQuantityExceeded'; // prettier-ignore
  const isRestaurantMaxDeliveryQuantityExceeded = responseType === 'RestaurantMaxDeliveryQuantityExceeded'; // prettier-ignore
  const isMaxQuantityErrorOccurred = isRestaurantMaxQuantityExceeded || isRestaurantMaxDeliveryQuantityExceeded; // prettier-ignore
  const isMaxModificationLimitExceeded = responseType === 'MaxModificationsExceeded'; // prettier-ignore
  const hasValidationErrors = responseType === 'ValidationError';
  const hasProductOutOfStockErrors = responseType === 'ProductOutOfStock';
  const isLocationClosed = responseType === 'LocationClosed';
  const isLineItemModificationInvalid = responseType === 'LineItemModificationsInvalidException'; // prettier-ignore

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const handleRestaurantMaxQuantityError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'caution' });
  }, [errorMessage, addNoticeBanner]);

  const handleMaxModificationLimitExceededError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'neutral' });
  }, [errorMessage, addNoticeBanner]);

  const handleValidationErrors = useCallback(() => {
    const fieldErrorMessage = responseData?.fieldErrors?.[0]?.message;

    if (!fieldErrorMessage) return;

    addNoticeBanner({ text: fieldErrorMessage, palette: 'caution' });
  }, [responseData, addNoticeBanner]);

  const handleProductOutOfStockError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'caution' });
  }, [errorMessage, addNoticeBanner]);

  const handleLocationClosedError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'caution' });
  }, [errorMessage, addNoticeBanner]);

  const handleLineItemModificationInvalidError = useCallback(() => {
    if (!errorMessage) return;

    addNoticeBanner({ text: errorMessage, palette: 'caution' });
  }, [errorMessage, addNoticeBanner]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isMaxQuantityErrorOccurred) {
      handleRestaurantMaxQuantityError();
    }

    if (isMaxModificationLimitExceeded) {
      handleMaxModificationLimitExceededError();
    }

    if (isLineItemModificationInvalid) {
      handleLineItemModificationInvalidError();
    }

    if (hasValidationErrors) {
      handleValidationErrors();
    }

    if (hasProductOutOfStockErrors) {
      handleProductOutOfStockError();
    }

    if (isLocationClosed) {
      handleLocationClosedError();
    }
  }, [
    addNoticeBanner,
    responseData,
    isMaxModificationLimitExceeded,
    isRestaurantMaxDeliveryQuantityExceeded,
    isRestaurantMaxQuantityExceeded,
    hasValidationErrors,
    isMaxQuantityErrorOccurred,
    isLocationClosed,
    hasProductOutOfStockErrors,
    handleRestaurantMaxQuantityError,
    handleMaxModificationLimitExceededError,
    handleValidationErrors,
    handleProductOutOfStockError,
    handleLocationClosedError,
    isLineItemModificationInvalid,
    handleLineItemModificationInvalidError,
  ]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type LineItemErrorResponseData =
  | Readonly<{
      __typename?: string;
      message?: string;
      fieldErrors?: ReadonlyArray<
        Readonly<{
          __typename?: 'FieldValidationError';
          field: string;
          message: string;
        }>
      >;
    }>
  | undefined;
